import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { ThemeToggle } from "@/components/ThemeToggle";
import { WA_RE, normalizeWa } from "@/components/WhatsAppButton";
import { apiError } from "@/lib/api";

const inputCls = "w-full rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent-gold)]";
const Field = ({ label, children }) => (<div><label className="mb-1 block text-xs font-medium text-[var(--text-muted)]">{label}</label>{children}</div>);

const FIELDS = [
  ["companyName", "Company Name"], ["tagline", "Tagline"], ["email", "Contact Email"], ["phone", "Phone"],
  ["whatsapp", "WhatsApp Number (with country code, e.g. +91 98765 43210)"], ["hours", "Working Hours"], ["address", "Address / City"], ["serviceNote", "Service Area Note"],
  ["instagram", "Instagram URL"], ["linkedin", "LinkedIn URL"],
];
const TEXTAREAS = [
  ["footerBlurb", "Footer Description", 2], ["aboutLine1", "About — Opening Line", 2], ["aboutLine2", "About — Gold Line", 2],
  ["aboutBody1", "About — Paragraph 1", 4], ["aboutBody2", "About — Paragraph 2", 3],
];

export const AdminSettings = () => {
  const { data, updateSettings, changePassword, resetData, currentUser } = useApp();
  const [form, setForm] = useState(data.settings || {});
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);

  useEffect(() => { setForm(data.settings || {}); }, [data.settings]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    const wa = normalizeWa(form.whatsapp);
    if (wa && !WA_RE.test(wa)) { toast.error("WhatsApp number must include country code, e.g. +91 98765 43210."); return; }
    setSaving(true);
    try { await updateSettings({ ...form, whatsapp: wa }); toast.success("Company details updated — live on the site."); }
    catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const submitPw = async (e) => {
    e.preventDefault();
    if (pw.next.length < 8) { toast.error("New password must be at least 8 characters."); return; }
    if (pw.next !== pw.confirm) { toast.error("Passwords do not match."); return; }
    setPwBusy(true);
    const res = await changePassword(pw.current, pw.next);
    setPwBusy(false);
    if (res.ok) { toast.success("Password changed."); setPw({ current: "", next: "", confirm: "" }); }
    else toast.error(res.error);
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1.5fr_1fr]">
      <div className="arch-card p-6" data-testid="company-settings">
        <div className="mb-1 text-sm font-medium text-[var(--text-primary)]">Company & About Details</div>
        <p className="mb-5 text-xs text-[var(--text-muted)]">Shown on Contact, About and the footer across the public site.</p>
        <div className="grid gap-3 sm:grid-cols-2">
          {FIELDS.map(([k, label]) => <Field key={k} label={label}><input value={form[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`setting-${k}`} className={inputCls} /></Field>)}
        </div>
        <div className="mt-3 space-y-3">
          {TEXTAREAS.map(([k, label, rows]) => <Field key={k} label={label}><textarea rows={rows} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`setting-${k}`} className={inputCls} /></Field>)}
        </div>
        <div className="mt-5 flex justify-end"><button onClick={save} disabled={saving} data-testid="save-settings" className="btn-gold !py-2 text-xs disabled:opacity-60">{saving ? "Saving…" : "Save Details"}</button></div>
      </div>

      <div className="space-y-6">
        <form onSubmit={submitPw} className="arch-card p-6" data-testid="change-password-form">
          <div className="mb-1 text-sm font-medium text-[var(--text-primary)]">Change Password</div>
          <p className="mb-4 text-xs text-[var(--text-muted)]">Signed in as {currentUser?.email}</p>
          <div className="space-y-3">
            <Field label="Current Password"><input type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} data-testid="pw-current" className={inputCls} autoComplete="current-password" /></Field>
            <Field label="New Password"><input type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} data-testid="pw-new" className={inputCls} autoComplete="new-password" /></Field>
            <Field label="Confirm New Password"><input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="pw-confirm" className={inputCls} autoComplete="new-password" /></Field>
          </div>
          <button type="submit" disabled={pwBusy} data-testid="pw-submit" className="btn-outline-gold mt-4 w-full !py-2 text-xs disabled:opacity-60">{pwBusy ? "Updating…" : "Update Password"}</button>
        </form>
        <div className="arch-card p-6"><div className="mb-3 text-sm font-medium text-[var(--text-primary)]">Appearance</div><div className="flex items-center justify-between"><span className="text-sm text-[var(--text-muted)]">Theme</span><ThemeToggle /></div></div>
        <div className="arch-card p-6"><div className="mb-2 text-sm font-medium text-[var(--text-primary)]">Data</div><p className="text-sm text-[var(--text-muted)]">Restore any missing demo records (sample properties, leads, team). Existing data is never deleted.</p><button onClick={async () => { if (window.confirm("Restore missing demo data?")) { await resetData(); toast.success("Demo data restored."); } }} data-testid="reset-data-btn" className="btn-outline-gold mt-4 !py-2 text-xs">Restore Demo Data</button></div>
      </div>
    </div>
  );
};

export default AdminSettings;
